import { useEffect, useState } from "react";
import { T } from "../theme";
import { apiCall } from "../apiClient";
import { Btn, Modal } from "./primitives";

type Direction = "firm" | "in_house";

interface Candidate {
  id: string;
  name: string;
  email: string;
  role: string;
  org_type: string;
  organization_name: string;
}

export const HandoffModal = ({
  open,
  onClose,
  onSuccess,
  requestId,
  direction,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess?: (newAssignee: string) => void;
  requestId: string;
  direction: Direction;
}) => {
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setErr(null);
    apiCall(`/requests/${requestId}/handoff-candidates?direction=${direction}`)
      .then((d) => setCandidates(d?.candidates || []))
      .finally(() => setLoading(false));
  }, [open, requestId, direction]);

  useEffect(() => {
    if (!open) { setSelected(""); setNote(""); setErr(null); }
  }, [open]);

  const title = direction === "firm" ? "Escalate to outside counsel" : "Return to in-house";
  const targetLabel = direction === "firm" ? "Firm user" : "In-house user";
  const endpoint = direction === "firm"
    ? `/requests/${requestId}/escalate-to-firm`
    : `/requests/${requestId}/return-to-in-house`;
  const bodyKey = direction === "firm" ? "firm_user_id" : "in_house_user_id";

  const submit = async () => {
    if (!selected) { setErr("Select a user"); return; }
    setSubmitting(true);
    setErr(null);
    const body: Record<string, any> = { [bodyKey]: selected };
    if (note.trim()) body.note = note.trim();
    const res = await apiCall(endpoint, { method: "POST", body: JSON.stringify(body) });
    setSubmitting(false);
    if (res?.ok) {
      onSuccess?.(selected);
      onClose();
    } else {
      setErr(res?.error || "Handoff failed");
    }
  };

  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose} title={title} w={520}>
      <div style={{ fontSize: 12, color: T.txD, marginBottom: 10 }}>
        {direction === "firm"
          ? "Hand this request to outside counsel for review. They will be notified and can take over the assignment."
          : "Hand this request back to the client's in-house team. They will be notified."}
      </div>

      <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: T.txD, marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.4 }}>{targetLabel} *</label>
      <select
        value={selected}
        onChange={(e) => setSelected(e.target.value)}
        disabled={loading}
        style={{ width: "100%", padding: "8px 10px", fontSize: 13, borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: "#FFFFFF", color: T.tx, fontFamily: "inherit", marginBottom: 12 }}
      >
        <option value="">{loading ? "Loading…" : "Select a user"}</option>
        {candidates.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name} ({c.role.replace(/_/g, " ")}) — {c.organization_name}
          </option>
        ))}
      </select>
      {!loading && candidates.length === 0 && (
        <div style={{ fontSize: 11, color: T.red, marginBottom: 12 }}>
          {direction === "firm"
            ? "No firm-side users have access to this client. Ask an administrator to grant access."
            : "No in-house users found for this client."}
        </div>
      )}

      <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: T.txD, marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.4 }}>Note (optional)</label>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Brief context for the recipient…"
        rows={3}
        style={{ width: "100%", padding: "8px 10px", fontSize: 12, borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box", marginBottom: 12 }}
      />

      {err && <div style={{ fontSize: 11, color: T.red, marginBottom: 10 }}>{err}</div>}

      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <Btn v="secondary" onClick={onClose}>Cancel</Btn>
        <Btn v="primary" onClick={submit} disabled={submitting || !selected}>
          {submitting ? "Submitting…" : (direction === "firm" ? "Escalate" : "Return to in-house")}
        </Btn>
      </div>
    </Modal>
  );
};
